import React, { useState, useRef, useEffect } from 'react';
import './Home.css';
import { FaSearch } from 'react-icons/fa';
import appIcon from '../assets/images/app_icon.png';

// Import all category data
import greetingsData from '../assets/1_greetings.json';
import introductionData from '../assets/2_introduction.json';
import familyData from '../assets/3_family.json';
import placesData from '../assets/4_places_things.json';
import foodData from '../assets/5_food.json';
import bodyData from '../assets/6_body.json';
import shoppingData from '../assets/7_shopping.json';
import travelData from '../assets/8_travel_and_direction.json';
import dailyData from '../assets/9_daily_activities.json';
import ceremoniesData from '../assets/10_ceremonies.json';
import fableData from '../assets/11_fable.json';
import adviceData from '../assets/12_useful_advice.json';
import oralData from '../assets/13_oral_history.json';
import stativeData from '../assets/14_stative_verbs.json';
import activeData from '../assets/15_active_verbs.json';

const categories = [
  { id: 'greetings', name: 'Greetings', icon: '👋', data: greetingsData },
  { id: 'introduction', name: 'Introduction', icon: '👨‍💼', data: introductionData },
  { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', data: familyData },
  { id: 'places', name: 'Places & Things', icon: '🏠', data: placesData },
  { id: 'food', name: 'Food', icon: '🍲', data: foodData },
  { id: 'body', name: 'Body', icon: '👤', data: bodyData },
  { id: 'shopping', name: 'Shopping', icon: '🛍️', data: shoppingData },
  { id: 'travel', name: 'Travel', icon: '✈️', data: travelData },
  { id: 'daily', name: 'Daily Activities', icon: '📅', data: dailyData },
  { id: 'ceremonies', name: 'Ceremonies', icon: '🎉', data: ceremoniesData },
  { id: 'fable', name: 'Fables', icon: '📚', data: fableData },
  { id: 'advice', name: 'Useful Advice', icon: '💡', data: adviceData },
  { id: 'oral', name: 'Oral History', icon: '🗣️', data: oralData },
  { id: 'stative', name: 'Stative Verbs', icon: '⚡', data: stativeData },
  { id: 'active', name: 'Active Verbs', icon: '🏃', data: activeData }
];

const Home = ({ selectedCategory, onSelectCategory }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const contentRef = useRef(null);
  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);

  const filteredPhrases = selectedCategoryData?.data.filter(phrase =>
    phrase.pulaar.toLowerCase().includes(searchTerm.toLowerCase()) ||
    phrase.english.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  useEffect(() => {
    if (selectedCategory && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedCategory]);

  return (
    <div className="home-container">
      {!selectedCategory ? (
        <div className="welcome-section">
          <h1>Welcome to Learn Pulaar</h1>
          <p>Learn the Pulaar language of Guinea. Select a category from below to start learning common Pulaar phrases and expressions.</p>
          <div className="featured-categories">
            <h2>Explore Categories</h2>
            <div className="category-grid">
              {categories.slice(0, 8).map(category => (
                <div 
                  key={category.id} 
                  className="category-card"
                  onClick={() => onSelectCategory(category.id)}
                >
                  <span className="category-icon">{category.icon}</span>
                  <h3>{category.name}</h3>
                </div>
              ))}
            </div>
          </div>

          <div className="video-section">
            <h2>Learn Pulaar with Video</h2>
            <div className="video-container">
              <iframe
                width="100%"
                height="500"
                src="https://www.youtube.com/embed/5Y0gdLfX9j4"
                title="Learn Pulaar Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          <section className="app-download-section">
            <div className="app-download-content">
              <div className="app-info">
                <h2>Download Our iOS App</h2>
                <p>Get the full Learn Pulaar experience on your iOS device. Learn on the go with our mobile app!</p>
                <ul className="app-features">
                  <li>Offline access to all lessons</li>
                  <li>Interactive quizzes and exercises</li>
                  <li>Audio pronunciation guides</li>
                  <li>Progress tracking</li>
                </ul>
                <a 
                  href="https://shorturl.at/Dbg6i" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="app-store-button"
                >
                  <img 
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                    alt="Download on the App Store"
                    className="app-store-badge"
                  />
                </a>
                <p className="coming-soon">Android version coming soon!</p>
              </div>
              <div className="app-preview">
                <img 
                  src={appIcon}
                  alt="Learn Pulaar iOS App Preview" 
                  className="app-screenshot"
                />
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div className="content-section" ref={contentRef}>
          <div className="content-header">
            <div className="header-left">
              <span className="category-icon">{selectedCategoryData.icon}</span>
              <h1>{selectedCategoryData.name}</h1>
            </div>
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search phrases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="phrases-container">
            {filteredPhrases.map((phrase, index) => (
              <div key={index} className="phrase-card">
                <div className="phrase-content">
                  <h3 className="phrase-pulaar">{phrase.pulaar}</h3>
                  <p className="phrase-english">{phrase.english}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 